import { useEffect, useState } from 'react'

import { Button, Card, Form, Icon, Row, Typography, notification } from 'antd'
import { Formik } from 'formik'
import * as yup from 'yup'

import { CustomBreadcrumb } from '../../components/CustomBreadcrumb/CustomBreadcrumb'
import CustomInput from '../../components/CustomInput/customInput'
import CustomSwitch from '../../components/CustomSwitch/customSwitch'
import SelectOrganization from '../../components/SelectOrganization/SelectOrganization'
import { RULES, validRule } from '../../constants/Entitlements'
import { api, request } from '../../util/handlerRequestUtil'
import { getMessage } from '../../util/IntlMessages'
import { isAdmin } from '../../util/profiles'

const CreateRegion = function ({ match, history }) {
  const defaultFormValues = { active: true }
  const [formLayout] = useState('vertical')

  // Params url
  const { id } = match.params

  // Form configuration
  const [formValues, setFormValues] = useState({})
  const isCurrentUserAdmin = isAdmin()

  const form = {
    initialValues: formValues,

    yupValidationCreate: yup.object().shape({
      // Create-validation-rules-body-marker
      active: yup
        .boolean()
        .typeError('must be boolean')
        .required(getMessage('isRequired', getMessage('active'))),

      name: yup
        .string()
        .matches(/[a-zA-Z]+/g, 'Preencha um nome válido')
        .max(60)
        .typeError('Preencha um nome válido')
        .nullable()
        .required(getMessage('isRequired', getMessage('regionName'))),

      dummy: yup.string(), // Used to organization dynamic validations
      organization: yup.string().when('dummy', {
        is: () => isCurrentUserAdmin,
        then: () => yup.string().required(getMessage('isRequired', getMessage('organization'))),
        otherwise: () => yup.string().nullable()
      })
    }),

    yupValidationUpdate: yup.object().shape({
      // Update-validation-rules-body-marker
      active: yup.boolean().typeError('must be boolean'),

      name: yup
        .string()
        .matches(/[a-zA-Z]+/g, 'Preencha um nome válido')
        .max(60)
        .nullable()
        .typeError('Preencha um nome válido'),

      dummy: yup.string(), // Used to organization dynamic validations
      organization: yup.string().when('dummy', {
        is: () => isCurrentUserAdmin,
        then: () => yup.string().required(getMessage('isRequired', getMessage('organization'))),
        otherwise: () => yup.string().nullable()
      })
    }),

    onSubmit: async (values) => {
      console.tron.log('submit values', values)
      if (!id) {
        const data = {
          organizationId: values.organization,
          name: values.name,
          active: values.active
        }

        storeRegion(data)
      } else {
        const payload = {
          data: {
            organizationId: values.organization,
            name: values.name,
            active: values.active
          },
          id
        }
        updateRegion(payload)
      }
    }
  }

  // Mount/Umount component
  useEffect(() => {
    const hasAccess = validRule(RULES.REGION) // Não deixa acessar a tela

    if (!hasAccess) {
      return history.push('/home')
    }

    if (id) {
      getRegionsById(id)
    } else {
      setFormValues({})
    }

    // Use-effect-mount-marker
    return () => {
      setFormValues({})
    }
  }, [])

  useEffect(() => {
    if (!id) {
      setFormValues(defaultFormValues)
    }
  }, [id])

  // Api requests
  async function storeRegion(data) {
    try {
      await request(api.storeRegion(data))
      notification.success({
        description: getMessage('successCreatedRegion'),
        message: getMessage('sweetAlerts.success')
      })
      setFormValues({})
      history.goBack()
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function getRegionsById(id) {
    const response = await request(api.getRegionsById(id))
    response.organization = response.organizationId
    setFormValues(response)
  }

  async function updateRegion(payload) {
    try {
      console.tron.log('updateRegion')
      const { id, data } = payload
      await request(api.updateRegion(id, data))
      notification.success({
        description: getMessage('successUpdateRegion'),
        message: getMessage('sweetAlerts.success')
      })
      history.goBack()
    } catch (error) {
      console.tron.log(error)
    }
  }

  function getTitle() {
    return id ? getMessage('updateRegion') : getMessage('createRegion')
  }

  function renderHeader(submitForm) {
    const hasAccess = !id ? validRule(RULES.REFERENCE_PERIOD) : validRule(RULES.REFERENCE_PERIOD)

    return (
      <div className="default-header-div">
        <div className="default-header-breadcrumb">
          <CustomBreadcrumb match={match} history={history} />
          <div className="default-header-buttons">
            <Button className="button-return" onClick={() => history.goBack()}>
              <Icon type="left" theme="outlined" className="default-back-icon" />
              {getMessage('back')}
            </Button>
            {hasAccess && (
              <Button type="primary" className="button-action" onClick={submitForm}>
                {id ? getMessage('update') : getMessage('register')}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }
  return (
    <>
      <Formik
        enableReinitialize
        validationSchema={!id ? form.yupValidationCreate : form.yupValidationUpdate}
        initialValues={formValues}
        onSubmit={(values, { setSubmitting, setFieldError, resetForm }) =>
          form.onSubmit(values, setSubmitting, setFieldError, resetForm)
        }
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, handleBlur, handleSubmit, setFieldValue, submitForm }) => {
          const setFieldValueWithState = (field, value) => {
            setFormValues({
              ...formValues,
              [field]: value
            })
            return setFieldValue(field, value)
          }

          return (
            <Form className="region-form" layout={formLayout} onSubmit={handleSubmit}>
              {renderHeader(submitForm)}

              <Card className="ant-card gx-card ant-card-bordered">
                <Typography.Title className="default-header-title" level={3}>
                  {getTitle()}
                </Typography.Title>

                <Row type="flex" justify="start">
                  <CustomSwitch
                    field="active"
                    errors={errors}
                    setFieldValue={setFieldValueWithState}
                    rowSpan={12}
                    value={values}
                    handleBlur={handleBlur}
                    unCheckedChildren={getMessage('label.inactive')}
                    checkedChildren={getMessage('label.active')}
                  />
                </Row>

                <Row type="flex" justify="start">
                  <CustomInput
                    field="name"
                    errors={errors}
                    setFieldValue={setFieldValueWithState}
                    rowSpan={12}
                    value={values}
                    handleBlur={handleBlur}
                    required
                  />
                  <SelectOrganization
                    errors={errors}
                    field="organization"
                    setFieldValue={setFieldValueWithState}
                    handleBlur={form.handleBlur}
                    formValues={formValues}
                    required
                  />
                </Row>
              </Card>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default CreateRegion
